import { observer } from 'mobx-react';
import * as React from 'react';
import styled from 'styled-components';
import { diContainer, serviceNames } from '../../service-setup/diContainer';
import * as classnames from 'classnames';
import { SearchGnavInputFieldViewController } from '../../internal/controllers/SearchGnavInputFieldViewController';
import { SearchCloseActiveScreen } from '../../internal/views/SearchCloseActiveScreen';
import { SearchOverlayActiveScreen } from '../../internal/views/SearchOverlayActiveScreen';
import { translate, ITranslationsCollection, ServiceView, config } from 'elc-service';
import { VOICE_SEARCH } from 'elc-service-view-names';
import { action } from 'mobx';

import {
    backgroundWhiteTheme,
    borderBottomTheme,
    filterHeadingTheme,
    clearButtonTheme,
    clearButtonThemeMobile,
    customInputTheme,
    customInputThemeMobile
} from '../../theme/default-theme';
import { ContentSpacing, breakpoint } from 'elc-base-theme';
import { SearchGrid } from '../../internal/views/SearchGrid';
import { Spinner } from 'elc-spinner';
import {
    SEARCH_PARAM,
    RESULTS_MESSAGE,
    GNAV_SEARCH_TAG_BLOCKS,
    SEARCH_VIEW,
    TOGGLE_TYPE,
    PREDICTIVE_SEARCH_PARAM,
    SEARCH_TYPE_PARAM_EXTERN,
    SEARCH_REDIRECT_PARAM
} from '../../internal/constants/Search';
import { IItem, IContentItem } from '../../internal/interfaces/ISearchInterfaces';
import { mergeConfigs } from '../../internal/utils/ConfigUtils';

const { NO_RESULTS, RESULTS_FOUND_OVERLAY, RESULTS_ADJUSTMENT } = RESULTS_MESSAGE;

const SearchActiveScreenWrapper = styled.div`
    position: fixed;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1000;
    overflow: auto;
    ${backgroundWhiteTheme};
`;

const InputGroup = styled.div`
    position: relative;
    width: 100%;
    display: table;
    border-collapse: collapse;
    ${borderBottomTheme};
`;

const ClearButton = styled.button`
    cursor: pointer;
    display: table-cell;
    vertical-align: middle;
    padding: 0;
    outline: inherit;
    margin-right: ${ContentSpacing.space12};
    margin-top: ${ContentSpacing.space48};
    ${filterHeadingTheme};
    ${clearButtonThemeMobile};
    [dir='rtl'] & {
        margin-left: ${ContentSpacing.space28};
    }

    ${breakpoint('desktop')`
        line-height: 30px;
        margin-top: ${ContentSpacing.space48};
        margin-right: ${ContentSpacing.space24};
        ${clearButtonTheme} 
        [dir='rtl'] & {
            margin-left: ${ContentSpacing.space24};
        }
    `};
`;

const InputSearchWrapper = styled.div`
    display: table-cell;
    vertical-align: middle;
    width: 100%;
    padding: ${ContentSpacing.space48} ${ContentSpacing.space12} ${ContentSpacing.space12}
        ${ContentSpacing.space12};
    ${breakpoint('desktop')`
        padding: ${ContentSpacing.space48} ${ContentSpacing.space24} ${ContentSpacing.space24} ${ContentSpacing.space24};
    `};

    .elc-floating-label-string {
        display: none;
    }

    .elc-input-search-field {
        height: auto;
        width: 100%;
        padding: 0;
        ${customInputThemeMobile};
        ${breakpoint('desktop')`
            ${customInputTheme};
        `};
    }
`;

const InputFieldSearch = styled.input`
    width: 100%;
`;

export interface ISearchGnavInputField {
    config: {
        baseConfig?: {
            content?: IContentItem[];
            productTags?: string[];
            items: IItem[];
            filterData: {
                filtersTitle: string;
                filtersDisplayType: string;
                generalKeys: object[];
                filterSets: object[];
            };
            productGridBreakpoints: object;
            hideProductPrice: boolean;
            hideProductBadge: boolean;
            showSubDisplayName: boolean;
            hideProductRating: boolean;
            enableNotifyMe: boolean;
            enableQuickView: boolean;
            enableMobileQuickView?: boolean;
            ctaType?: string;
        };
        content?: IContentItem[];
        productTags?: string[];
        items: IItem[];
        filterData: {
            filtersTitle: string;
            filtersDisplayType: string;
            generalKeys: object[];
            filterSets: object[];
        };
        productGridBreakpoints: object;
        hideProductPrice: boolean;
        hideProductBadge: boolean;
        showSubDisplayName?: boolean;
        hideProductRating: boolean;
        enableNotifyMe?: boolean;
        enableQuickView: boolean;
        hideSorting: boolean;
        enableMobileQuickView?: boolean;
        ctaType?: string;
        searchUrl?: string;
        minSearchLength?: number;
        searchDisplayFormat: string;
        gnavSearchTagsBlock?: boolean;
        hideGnavInput?: boolean;
        enableVoiceSearch?: boolean;
        phrases?: string[];
        silenceTimeout?: number;
        stopTimeout?: number;
        enableAutoReplenishment?: boolean;
        autoReplenishOptions?: number[];
        autoReplenishToggleType?: TOGGLE_TYPE;
    };
    toggleSearchOverlay?(): void;
    translations?: ITranslationsCollection;
}

@config(['searchConfig'], mergeConfigs)
@translate(['searchClearLabel', 'searchPlaceholderLabel', 'searchButtonLabel'])
@observer
export class SearchGnavInputField extends React.Component<ISearchGnavInputField> {
    private searchGnavInputFieldViewController: SearchGnavInputFieldViewController;
    public static defaultProps: ISearchGnavInputField;
    private hideOverlay: boolean = true;
    private inputRef: React.RefObject<HTMLInputElement> = React.createRef();

    constructor(props: ISearchGnavInputField) {
        super(props);
        this.searchGnavInputFieldViewController = diContainer.get(
            serviceNames.searchGnavInputFieldViewController
        );

        if (props.config.minSearchLength) {
            this.searchGnavInputFieldViewController.setMinSearchLength(
                props.config.minSearchLength
            );
        }

        this.searchGnavInputFieldViewController.setSearchDisplayFormat(
            props.config.searchDisplayFormat
        );
    }

    public onKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && this.searchGnavInputFieldViewController.data.inputValue) {
            this.hideOverlay = false;
            const searchUrl = this.props.config.searchUrl;
            const searchParam = SEARCH_PARAM;
            const value = event.currentTarget.value;
            let redirect = false;
            let siteUrl = `${searchUrl}${searchParam}${value}`;

            if (!!this.searchGnavInputFieldViewController.endecaRedirectUrl) {
                siteUrl = `${this.searchGnavInputFieldViewController.endecaRedirectUrl}${SEARCH_TYPE_PARAM_EXTERN}${PREDICTIVE_SEARCH_PARAM}${SEARCH_REDIRECT_PARAM}`;
                redirect = true;
            }

            this.searchGnavInputFieldViewController.onKeyUp(
                { key: event.key, value },
                siteUrl,
                redirect
            );
        }
    };

    public handleChange = (event: React.FormEvent<HTMLInputElement>) => {
        const value = event.currentTarget.value;
        const body = document.body;
        if ((this.props.config.gnavSearchTagsBlock && value) || value !== '') {
            body.classList.remove(`${GNAV_SEARCH_TAG_BLOCKS}`);
        } else if (this.props.config.gnavSearchTagsBlock) {
            body.classList.add(`${GNAV_SEARCH_TAG_BLOCKS}`);
        }
        this.searchGnavInputFieldViewController.handleChange({ value });
    };

    public get showSearchResults() {
        if (!!this.searchGnavInputFieldViewController.endecaRedirectUrl) {
            return false;
        }

        let items = [];

        if (this.props.config.baseConfig) {
            items = this.props.config.baseConfig.items;
        } else {
            items = this.props.config.items;
        }

        if (
            this.searchGnavInputFieldViewController.data.inputValue &&
            (items || this.searchGnavInputFieldViewController.data.items.length > 0)
        ) {
            return true;
        }

        return false;
    }

    public toggleSearchOverlay = () => {
        this.searchGnavInputFieldViewController.toggleSearchOverlay();
        const body = document.body;
        const html = document.documentElement;
        if (this.searchGnavInputFieldViewController.isSearchOverlayOpen) {
            html.classList.add('search-overlay-displayed');
            body.classList.add('search-overlay-displayed');
            if (this.props.config.gnavSearchTagsBlock) {
                body.classList.add(`${GNAV_SEARCH_TAG_BLOCKS}`);
            }
        } else {
            html.classList.remove('search-overlay-displayed');
            body.classList.remove('search-overlay-displayed');
            if (this.props.config.gnavSearchTagsBlock) {
                body.classList.remove(`${GNAV_SEARCH_TAG_BLOCKS}`);
            }
        }
    };

    private getResultType = () => {
        const { numRecs, adjustedTerm } = this.searchGnavInputFieldViewController.data;

        if (numRecs.totalNumRecs === 0) {
            return NO_RESULTS;
        }
        if (adjustedTerm) {
            return RESULTS_ADJUSTMENT;
        }

        return RESULTS_FOUND_OVERLAY;
    };

    @action
    public onOverlayScroll = (e: React.SyntheticEvent<HTMLDivElement>) => {
        if (e.currentTarget.scrollTop > 10) {
            (this.inputRef.current as HTMLInputElement).blur();
        }
    };

    public render() {
        const searchActiveScreenWrapper = classnames(
            'elc-search-active-screen-wrapper',
            'js-search-active-screen-wrapper'
        );
        const searchInputGroupClassnames = classnames(
            'elc-search-input-group',
            'js-search-input-group'
        );
        const searchInputWrapperClassnames = classnames(
            'elc-input-search-wrapper',
            'js-input-search-wrapper'
        );
        const clearButtonClassnames = classnames(
            'elc-search-clear-button',
            'js-search-clear-button'
        );
        const voiceSearchWrapperClassnames = classnames(
            'elc-voice-search-wrapper',
            'js-voice-search-wrapper'
        );

        const {
            data,
            clearInputValue,
            searchTermSelected
        } = this.searchGnavInputFieldViewController;

        const {
            limitItems,
            inputValue,
            resultsLoaded,
            isSearchOverlayOpen,
            disableInput,
            limiteTypeaheadResults,
            typeaheadResults,
            numRecs,
            adjustedTerm,
            configSearch
        } = data;

        const { searchClearLabel, searchPlaceholderLabel, searchButtonLabel } = this.props
            .translations as ITranslationsCollection;
        const {
            searchUrl,
            enableVoiceSearch,
            phrases,
            silenceTimeout,
            stopTimeout,
            enableAutoReplenishment,
            autoReplenishOptions,
            autoReplenishToggleType,
            hideGnavInput,
            items: bestSellers
        } = this.props.config;

        let searchConfig;

        if (this.props.config.baseConfig) {
            searchConfig = {
                ...this.props.config.baseConfig,
                searchItems: limitItems,
                bestSellerItems: this.props.config.baseConfig.items,
                searchUrl,
                enableAutoReplenishment,
                autoReplenishOptions,
                autoReplenishToggleType
            };
        } else {
            searchConfig = {
                ...this.props.config,
                searchItems: limitItems,
                bestSellerItems: bestSellers,
                searchUrl
            };
        }

        const voiceSearchConfig = {
            searchUrl,
            phrases,
            silenceTimeout,
            stopTimeout
        };

        const searchInputFieldClassnames = classnames(
            'elc-input-search-field',
            'js-input-search-field',
            {
                'voice-search-enabled': enableVoiceSearch
            }
        );

        return (
            <>
                <SearchOverlayActiveScreen
                    toggleSearchOverlay={this.toggleSearchOverlay}
                    isSearchOverlayOpen={isSearchOverlayOpen}
                    hideGnavInput={hideGnavInput}
                />
                {isSearchOverlayOpen && (
                    <SearchActiveScreenWrapper
                        className={searchActiveScreenWrapper}
                        onScroll={this.onOverlayScroll}
                    >
                        <SearchCloseActiveScreen toggleSearchOverlay={this.toggleSearchOverlay} />
                        <InputGroup className={searchInputGroupClassnames}>
                            <InputSearchWrapper className={searchInputWrapperClassnames}>
                                <InputFieldSearch
                                    disabled={disableInput}
                                    name="search-input"
                                    type="text"
                                    placeholder={searchPlaceholderLabel}
                                    onChange={this.handleChange}
                                    value={inputValue}
                                    className={searchInputFieldClassnames}
                                    onKeyUp={this.onKeyUp}
                                    data-test-id="SEA-Keyword-Text"
                                    aria-label={searchPlaceholderLabel}
                                    title={searchButtonLabel}
                                    autoFocus
                                    innerRef={this.inputRef}
                                />
                            </InputSearchWrapper>
                            {inputValue && (
                                <ClearButton
                                    data-test-id="SEA-Clear"
                                    className={clearButtonClassnames}
                                    onClick={clearInputValue}
                                >
                                    {searchClearLabel}
                                </ClearButton>
                            )}
                        </InputGroup>
                        {this.showSearchResults && this.hideOverlay && resultsLoaded ? (
                            <SearchGrid
                                config={searchConfig}
                                inputValue={inputValue}
                                resultsLoaded={resultsLoaded}
                                isLoading={this.searchGnavInputFieldViewController.isLoading}
                                limiteTypeaheadResults={limiteTypeaheadResults}
                                typeaheadResults={typeaheadResults}
                                numRecs={numRecs}
                                adjustedTerm={adjustedTerm}
                                resultType={this.getResultType()}
                                searchTermSelected={searchTermSelected}
                                view={SEARCH_VIEW.OVERLAY}
                            />
                        ) : (
                            <Spinner
                                data-test-id="SEA-SpinnerIcon"
                                isLoading={this.searchGnavInputFieldViewController.isLoading}
                            />
                        )}
                        {!!(enableVoiceSearch && configSearch.voiceSearch.enabled) && (
                            <div className={voiceSearchWrapperClassnames}>
                                <ServiceView name={VOICE_SEARCH} config={voiceSearchConfig} />
                            </div>
                        )}
                    </SearchActiveScreenWrapper>
                )}
            </>
        );
    }
}

export default SearchGnavInputField;
